import { ISidebarItem, Services } from '@brand-console/types'
import {
  CustomerEngagementIcon,
  FeedMarketingIcon,
  FulfillmentIcon,
  GrowthMarketingIcon,
  MarketplaceManagementIcon,
  MarketplaceServicesIcon,
  StorefrontIcon,
  UnifiedAnalyticsIcon,
} from '@cart/ui'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import slugster from 'slugster'

import { FeedMarketing } from '../../components/FeedMarketing'
import { Logout } from '../../components/Logout'
import { MarketplaceManagement } from '../../components/MarketplaceManagement'
import { Storefront } from '../../components/Storefront'
import { AdminTools } from '../AdminTools'
import { CannyFeatureRequest } from '../FeatureRequests/FeatureRequests'
import { FreeToolsRoutes } from '../FreeToolsRoutes/FreeToolsRoutes'
import { Homepage } from '../Homepage'
import { LoginSignUpRoutes } from '../LoginSignUpRoutes'
import { OrganizationSelectionRoute } from '../OrganizationSelectionRoute/OrganizationSelectionRoute'
import { CreateOrganization } from '../OrganizationsRoutes/CreateOrganization/CreateOrganization'
import { CustomerEngagementRoute } from '../ProductPages/CustomerEngagementRoute'
import { FulfillmentRoute } from '../ProductPages/FulfillmentRoute'
import { GrowthMarketingRoute } from '../ProductPages/GrowthMarketingRoute'
import { MarketplaceServicesRoute } from '../ProductPages/MarketplaceServicesRoute'
import { SettingsRoute } from '../SettingsRoute/SettingsRoute'
import { StatusRoute } from '../StatusRoute'
import { INavigationProps } from './RoutesConfig.types'
import { Reporting } from '../../components/Reporting'

export const getSiteNavigation = async ({
  hasAdminToolsEnabled = false,
  hasSFEnabled = false,
  hasMCMEnabled = false,
}: INavigationProps = {}): Promise<ISidebarItem[]> => {
  return [
    {
      component: Homepage,
      exactRoute: true,
      footer: true,
      label: 'Home',
      link: '/',
      route: ['/', '/solutions-services'],
      children: [
        {
          component: FulfillmentRoute,
          footer: false,
          icon: <FulfillmentIcon />,
          label: Services.FULFILLMENT,
          link: '/fulfillment',
          route: ['/fulfillment'],
          sidebar: false,
        },
        {
          component: StatusRoute,
          route: ['/status'],
        },
        {
          component: CreateOrganization,
          route: ['/organization/new'],
        },
        {
          component: LoginSignUpRoutes,
          route: ['/login', '/signup'],
        },
        {
          component: Logout,
          route: '/logout',
        },
        {
          component: OrganizationSelectionRoute,
          route: '/organization-selection',
        },
        {
          component: CannyFeatureRequest,
          route: '/feature-requests',
        },
        {
          component: SettingsRoute,
          footer: true,
          icon: <FontAwesomeIcon icon={solid('cog')} />,
          label: 'Settings',
          link: '/settings',
          route: ['/settings/*'],
          sidebar: true,
          children: [
            {
              icon: <FontAwesomeIcon icon={solid('user-circle')} />,
              label: 'Profile',
              link: '/settings/profile',
              sidebar: true,
              footer: true,
            },
            {
              icon: <FontAwesomeIcon icon={solid('lock')} />,
              label: 'Security',
              link: '/settings/security',
              sidebar: true,
              footer: true,
            },
            {
              icon: <FontAwesomeIcon icon={solid('building')} />,
              label: 'Organization',
              link: '/settings/organization',
              sidebar: true,
              footer: true,
            },
            ...(hasSFEnabled
              ? [
                {
                  icon: <FontAwesomeIcon icon={solid('shopping-cart')} />,
                  label: Services.STOREFRONT,
                  link: '/settings/storefront',
                  route: ['/settings/storefront', '/settings/online-store'],
                  sidebar: true,
                  footer: true,
                },
              ]
              : []),
            ...(hasMCMEnabled
              ? [
                {
                  icon: <FontAwesomeIcon icon={solid('layer-group')} />,
                  label: Services.MARKETPLACE_MANAGEMENT,
                  link: `/settings/${slugster(Services.MARKETPLACE_MANAGEMENT)}`,
                  route: [
                    '/settings/marketplace-management',
                    '/settings/multichannel-management',
                  ],
                  sidebar: true,
                  footer: true,
                },
              ]
              : []),
            ...(hasAdminToolsEnabled
              ? [
                {
                  component: AdminTools,
                  footer: false,
                  icon: <FontAwesomeIcon icon={solid('cogs')} />,
                  label: 'Admin Tools',
                  link: '/settings/admin-tools',
                  route: ['/settings/admin-tools'],
                  sidebar: false,
                },
              ]
              : []),
          ],
        },
        {
          component: FreeToolsRoutes,
          route: '/free-tools/*',
        },
        {
          component: Storefront,
          footer: true,
          icon: <StorefrontIcon />,
          label: Services.STOREFRONT,
          link: '/storefront',
          route: ['/sflogin', '/sfsignup', '/storefront', '/online-store'],
          sidebar: false,
        },
        {
          component: MarketplaceManagement,
          footer: true,
          icon: <MarketplaceManagementIcon />,
          label: Services.MARKETPLACE_MANAGEMENT,
          link: `/${slugster(Services.MARKETPLACE_MANAGEMENT)}`,
          route: [
            '/marketplace-management',
            `/${slugster(Services.MARKETPLACE_MANAGEMENT)}`,
            '/multichannel-management',
          ],
          sidebar: false,
        },
        {
          component: Reporting,
          footer: true,
          icon: <UnifiedAnalyticsIcon />,
          label: Services.REPORTING,
          link: `/${slugster(Services.REPORTING)}`,
          route: [
            `/${slugster(Services.REPORTING)}`,
          ],
          sidebar: false,
        },
        {
          component: FeedMarketing,
          footer: true,
          icon: <FeedMarketingIcon />,
          label: Services.FEED_MARKETING,
          link: '/feed-marketing',
          route: ['/dfw', '/feed-management/*', '/feed-marketing/*'],
          sidebar: false,
        },
        {
          component: GrowthMarketingRoute,
          footer: false,
          icon: <GrowthMarketingIcon />,
          label: Services.GROWTH_MARKETING,
          link: '/growth-marketing',
          route: ['/growth-marketing', '/marketing-services'],
          sidebar: false,
        },
        {
          component: CustomerEngagementRoute,
          footer: true,
          icon: <CustomerEngagementIcon />,
          label: 'Customer Engagement',
          link: '/customer-engagement',
          route: ['/customer-engagement', '/customer-service'],
          sidebar: false,
        },
        {
          component: MarketplaceServicesRoute,
          footer: false,
          icon: <MarketplaceServicesIcon />,
          label: 'Marketplace Services',
          link: '/marketplace-services',
          route: ['/marketplace-services', '/business-planning'],
          sidebar: false,
        },
      ],
    },
  ]
}
